import React, { useCallback, useMemo } from 'react';
import { Point, VictoryScatter, VictoryTooltip } from 'victory';
import { DomainTuple, VictoryLabel } from 'victory-core';
import { colors } from '../../../../colors';
import axios from 'axios';
import { PriceChartApiResponse } from '../../../../screens/Detail/Charts';
import { getTimeFrameIntervalTimestamp } from '../../../../screens/Detail/Charts/PriceChart/helpers';
import {
  generateTooltipLabelGenericTransaction,
  isCustomTransaction,
} from '../../../../helpers/bundleTransactionsToCompactForm';
import ServicePaths from '../../../../bridge/utils/ServicePaths';
import { Bot } from 'grts-utils/types/Bot';
import { GenericTransactionInfo } from '../../../../types/Transaction';
import { CustomTransaction } from 'grts-utils/types/Transaction';

type DataPoint = {
  low: number;
  open: number;
  close: number;
  high: number;
  x: number;
  x2: number;
};

interface Props {
  baseAsset: string | undefined;
  quoteAsset: string | undefined;
  data: DataPoint[];
  candleResponse: PriceChartApiResponse | undefined;
  temporaryCandles: PriceChartApiResponse | undefined;
  setTemporaryCandles: React.Dispatch<
    React.SetStateAction<PriceChartApiResponse | undefined>
  >;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  transactions?: Array<GenericTransactionInfo | CustomTransaction>;
  bot?: Bot | undefined;
  transactionsByTimestamp?: Record<string, GenericTransactionInfo[]>;
  transactionsSetsLength?: number;
  multiOrderCandles?: PriceChartApiResponse | undefined;
  getIndividualTransactions?: (
    isProfitTransaction: boolean,
    compactTransactionId: string | null,
  ) => GenericTransactionInfo[];
  domain:
    | {
        x: DomainTuple;
        y: DomainTuple;
      }
    | undefined;
  zoomLevel: number;
  setZoomLevelDomainX: React.Dispatch<
    React.SetStateAction<[number, number] | undefined>
  >;
  setZoomLevelRange: React.Dispatch<React.SetStateAction<number | undefined>>;
}

type Datum = {
  x: number;
  multiOrderTimeFrameX?: number;
  y: number;
  color: string;
  t: GenericTransactionInfo | CustomTransaction;
  isProfitCalcs: boolean;
  label?: string;
  compactTransactionTimestamp?: number;
  size?: number;
  strokeColor?: string;
};

const TransactionDotsChart: React.FC<Props> = (props: Props) => {
  const {
    baseAsset,
    quoteAsset,
    data,
    candleResponse,
    temporaryCandles,
    setTemporaryCandles,
    domain,
    setIsLoading,
    bot,
    transactions,
    transactionsByTimestamp,
    transactionsSetsLength,
    multiOrderCandles,
    getIndividualTransactions,
    zoomLevel,
    setZoomLevelDomainX,
    setZoomLevelRange,
  } = props;

  const latestOrder = bot?.runtime?.latestOrder;
  const timeFrames = bot?.timeFrame;
  const multiOrder = bot?.runtime?.multiOrder;

  const findCandleStartTimestamp = useCallback(
    (timestamp: number) =>
      data.find((d) => d.x <= timestamp && timestamp <= d.x2)?.x ?? timestamp,
    [data],
  );

  const findMultiOrderCandleStartTimestamp = useCallback(
    (timestamp: number) => {
      return multiOrderCandles?.normal?.find(
        (d) => d.startTimestamp <= timestamp && timestamp <= d.endTimestamp,
      );
    },
    [multiOrderCandles],
  );

  const transactionsDataSets = useMemo(() => {
    const transactionsDataSets: Array<Datum> = [];

    transactions?.forEach((transaction, i) => {
      const isProfitCalcs = i === 1;
      const timestamp = transaction.transactionTimestamp;

      const multiOrder = findMultiOrderCandleStartTimestamp(timestamp);

      const multiOrderTimeFrameX = multiOrder?.startTimestamp || timestamp;

      const x =
        data.find((d) => d.x <= timestamp && timestamp <= d.x2)?.x ?? timestamp;

      const color = isCustomTransaction(transaction)
        ? colors.blue
        : transaction.orderType === 'BUY'
        ? colors.green
        : colors.red;

      // if (transactionsByTimestamp) {
      //   const isTransactionInSync =
      //     transactionsByTimestamp[multiOrderTimeFrameX]?.length > 1;

      //   color = isTransactionInSync ? colors.green : colors.red;
      // } else {
      //   color = colors.white;
      // }

      const isInView =
        domain && (domain.x[0] as number) <= x && x <= (domain.x[1] as number);

      if (isInView && temporaryCandles) {
        const individualTransactions = getIndividualTransactions?.(
          isProfitCalcs,
          isCustomTransaction(transaction)
            ? null
            : transaction.compactTransactionId ?? null,
        );

        if (individualTransactions) {
          transactionsDataSets.push(
            ...individualTransactions.map((individualTransaction) => {
              const timestamp = individualTransaction.transactionTimestamp;

              const multiOrderTimeFrameX =
                findMultiOrderCandleStartTimestamp(timestamp)?.startTimestamp ||
                timestamp;

              const x =
                data.find((d) => d.x <= timestamp && timestamp <= d.x2)?.x ??
                timestamp;

              return {
                x,
                multiOrderTimeFrameX,
                y: individualTransaction.orderPrice || 0,
                color,
                t: individualTransaction,
                isProfitCalcs,
                compactTransactionTimestamp: transaction.transactionTimestamp,
              };
            }),
          );

          return;
        }
      }

      transactionsDataSets.push({
        x,
        multiOrderTimeFrameX,
        y: isCustomTransaction(transaction)
          ? multiOrder?.close || 0
          : transaction.orderPrice || 0,
        color,
        t: transaction,
        isProfitCalcs,
        compactTransactionTimestamp: transaction.transactionTimestamp,
        strokeColor: transaction.transactionType.includes('STOP_LOSS')
          ? colors.blue
          : transaction.transactionType.includes('TAKE_PROFIT')
          ? colors.yellow
          : colors.white,
        size: transaction.transactionType.includes('STOP_LOSS')
          ? 3
          : transaction.transactionType.includes('TAKE_PROFIT')
          ? 3
          : 5,
      });

      transactionsDataSets.sort((a, b) => a.x - b.x);

      transactionsDataSets.forEach((datum) => {
        datum.label = generateTooltipLabelGenericTransaction(datum.t);
      });
    });

    const filteredTransactionsDataSets = transactionsDataSets.filter(
      (v, i, s) =>
        s.findIndex(
          (t) => t.multiOrderTimeFrameX === v.multiOrderTimeFrameX,
        ) === i,
    );

    return transactionsSetsLength === 1
      ? transactionsDataSets
      : filteredTransactionsDataSets;
  }, [
    data,
    domain,
    findMultiOrderCandleStartTimestamp,
    getIndividualTransactions,
    temporaryCandles,
    transactions,
    transactionsSetsLength,
  ]);

  const transactionsDataPoints = useMemo(() => {
    if (!candleResponse || !multiOrder || !Object.keys(multiOrder).length) {
      return null;
    }

    // @TODO if we enable multi order again
    const amount = 1;

    if (!timeFrames?.multiOrder) {
      return null;
    }

    const interval = getTimeFrameIntervalTimestamp(timeFrames?.multiOrder);
    const dataPoints = [];

    const normalCandles = temporaryCandles?.normal ?? candleResponse.normal;

    for (let i = 0; i < amount; i++) {
      let datum;

      const startTimestamp =
        normalCandles[normalCandles.length - 1].startTimestamp;

      for (let y = normalCandles.length - 1; y > 0; y--) {
        if (
          normalCandles[y].startTimestamp <= startTimestamp - interval * i &&
          startTimestamp - interval * i <= normalCandles[y].endTimestamp
        ) {
          datum = normalCandles[y];
          break;
        }
      }

      if (datum) {
        dataPoints.push({
          x: datum.startTimestamp,
          y: datum.close,
          color: latestOrder
            ? latestOrder === 'BUY'
              ? colors.green
              : colors.red
            : colors.white,
        });
      }
    }

    /** Filter on unique timestamps */
    return dataPoints.filter(
      (v, i, s) => s.findIndex((d) => d.x === v.x) === i,
    );
  }, [
    candleResponse,
    latestOrder,
    multiOrder,
    temporaryCandles?.normal,
    timeFrames?.multiOrder,
  ]);

  const handleZoomIn = useCallback(async () => {
    /** Switch view to shorter/multiT candles */

    /** Don't handle the press a second time */
    if (temporaryCandles) {
      return;
    }

    /**
     * Find the startTimestamp
     * find the startDate of the higherT candle
     * Of the start of the previous wave
     */
    let startTimestamp;
    let startOfCurrentWaveFound = false;

    if (!candleResponse?.heikin?.length) {
      return;
    }

    for (let i = candleResponse.heikin.length - 1; i > 0; i--) {
      const candle = candleResponse.heikin[i];

      if (candle.startOfNewTrend) {
        if (!startOfCurrentWaveFound) {
          startOfCurrentWaveFound = true;
          continue;
        } else {
          startTimestamp = candle.startTimestamp;
          break;
        }
      }
    }

    if (!startTimestamp) {
      return;
    }

    const timeFrame = timeFrames?.multiOrder;

    startTimestamp =
      new Date().valueOf() - (new Date().valueOf() - startTimestamp) / 3;

    /** Now we can fetch the candles for this timeFrame
     * Put them in a state
     * and display them
     */
    const { data }: { data: PriceChartApiResponse } = await axios({
      method: 'GET',
      url: ServicePaths.CANDLE_FETCHER.FetchCandles,
      params: {
        baseAsset,
        quoteAsset,
        timeFrame,
        startTimestamp,
        endTimestamp: new Date().valueOf(),
        includeLatestCandle: true,
      },
    });

    setTemporaryCandles(data);
  }, [
    baseAsset,
    candleResponse?.heikin,
    quoteAsset,
    setTemporaryCandles,
    temporaryCandles,
    timeFrames?.multiOrder,
  ]);

  const calculateStartOfWaveTimestamp = useCallback(
    (timestamp: number) => {
      const heikinCandles = candleResponse?.heikin;

      if (!heikinCandles?.length) {
        return timestamp;
      }

      const candleStartTimestamp = findCandleStartTimestamp(timestamp);

      const candleIndex = heikinCandles.findIndex(
        (c) => c.startTimestamp === candleStartTimestamp,
      );

      if (candleIndex === -1) {
        return timestamp;
      }

      /** Find the first heikinCandle with a startOfTrends true */
      let startOfWaveCandle;

      for (let index = candleIndex; index >= 0; index--) {
        const candle = heikinCandles[index];

        if (candle.startOfNewTrend) {
          startOfWaveCandle = candle;
          break;
        }
      }

      return startOfWaveCandle?.startTimestamp || timestamp;
    },
    [candleResponse?.heikin, findCandleStartTimestamp],
  );

  const getZoomLevelTimeFrame = useCallback(
    (zoomLevel: number) => {
      switch (zoomLevel) {
        case 4:
        case 3:
          return bot?.timeFrame?.multiOrder;

        case 2:
          return bot?.timeFrame?.shorter;

        default:
          return bot?.timeFrame?.higher;
      }
    },
    [
      bot?.timeFrame?.higher,
      bot?.timeFrame?.multiOrder,
      bot?.timeFrame?.shorter,
    ],
  );

  const handleTransactionPress = useCallback(
    async (
      event: React.SyntheticEvent<any, Event>,
      eventProps: { index: number; data: Datum[]; datum: Datum },
    ) => {
      if (
        !bot ||
        !transactions ||
        !transactionsByTimestamp ||
        temporaryCandles
      ) {
        return;
      }

      if (isCustomTransaction(eventProps.datum.t)) {
        return;
      }

      setIsLoading?.(true);

      /**
       * This function will fetch candles for the zoomedIn state of the transactions
       *
       * Get the transaction timestamp
       * Find the respective transaction
       * (I think it's best to pair them together already before this function?)
       *
       * Fetch the candles in between them + extra range
       */

      // const isProfitCalcs = eventProps.datum.isProfitCalcs;
      const realTransactions = transactions;
      // const profitCalcsTransactions = transactions[1];
      // const otherTransactionSet = isProfitCalcs
      //   ? realTransactions
      //   : profitCalcsTransactions;

      // const currentTransactionSet = isProfitCalcs
      //   ? profitCalcsTransactions
      //   : realTransactions;

      const currentTransactionSet = realTransactions;

      const getEndTimestamp = (
        transaction: GenericTransactionInfo,
        startTimestamp: number,
      ) => {
        const transactionIndex = currentTransactionSet
          .sort((a, b) => a.transactionTimestamp - b.transactionTimestamp)
          .findIndex(
            (t) => t.transactionTimestamp === transaction.transactionTimestamp,
          );

        if (
          transactionIndex !== -1 &&
          transactionIndex < currentTransactionSet.length - 1
        ) {
          const nextTransaction = currentTransactionSet[transactionIndex + 1];

          return nextTransaction.transactionTimestamp;
        }

        return (
          transaction.transactionTimestamp +
          (transaction.transactionTimestamp - startTimestamp) * 0.4
        );
      };

      let startTimestamp, endTimestamp;

      if (transactionsSetsLength === 1) {
        /** Start of the wave until next transaction or end */
        startTimestamp = calculateStartOfWaveTimestamp(eventProps.datum.x);
        endTimestamp = getEndTimestamp(eventProps.datum.t, startTimestamp);
      } else {
        const transactionTimestamp = eventProps.datum.t.transactionTimestamp;
        // (isProfitCalcs
        //   ? getTimeFrameIntervalTimestamp(
        //       bot?.timeFrame?.multiOrder || TimeFrame.OneWeek,
        //     )
        //   : 0);

        const x =
          findMultiOrderCandleStartTimestamp(transactionTimestamp)
            ?.startTimestamp || transactionTimestamp;
        const linkedTransactions = transactionsByTimestamp[x];

        /**
         * if undefined, no handle
         * if length 1, we haave to find the other trasnsaction (same orderType)
         * if length is 2, they are correctly synced
         */

        if (typeof linkedTransactions === 'undefined') {
          setIsLoading?.(false);
          return;
        }

        const transaction = linkedTransactions[0];

        if (linkedTransactions.length === 1) {
          /** if length 1, we have to find the other transaction (same orderType)
           * Find the transaction with the smallest abs diff with our
           * current transaction timestamp and same orderType
           *
           * If there is no other transaction set,
           * just zoom in on the transaction timestamp
           */

          // if (!profitCalcsTransactions?.length) {
          /** Zoom in on the current transaction
           * Get the start of the current wave
           * End is next transaction or *now*
           */
          const startOfWaveTimestamp =
            calculateStartOfWaveTimestamp(transactionTimestamp);

          startTimestamp = startOfWaveTimestamp;
          endTimestamp = getEndTimestamp(transaction, startTimestamp);
          // } else {
          //   /**
          //    * Find the transaction with the smallest abs diff with our
          //    * current transaction timestamp and same orderType
          //    */
          //   const closestTransaction = otherTransactionSet.reduce(
          //     (acc, curr) => {
          //       if (acc === null) {
          //         return transaction.orderType !== curr.orderType ? acc : curr;
          //       }

          //       if (
          //         Math.abs(curr.transactionTimestamp - transactionTimestamp) <=
          //           Math.abs(acc.transactionTimestamp - transactionTimestamp) &&
          //         transaction.orderType === curr.orderType
          //       ) {
          //         return curr;
          //       }

          //       return acc;
          //     },
          //     null as Transaction | null,
          //   );

          //   if (!closestTransaction) {
          //     /** Show start of wave until next transaction or *now* */
          //     const startOfWaveTimestamp =
          //       calculateStartOfWaveTimestamp(transactionTimestamp);

          //     startTimestamp = startOfWaveTimestamp;
          //     endTimestamp = getEndTimestamp(transaction, startTimestamp);
          //   } else {
          //     /** Show from min timestamp until max timestamp + range */
          //     startTimestamp = Math.min(
          //       Math.min(
          //         transaction.transactionTimestamp,
          //         closestTransaction.transactionTimestamp,
          //       ),
          //       calculateStartOfWaveTimestamp(
          //         Math.min(
          //           closestTransaction.transactionTimestamp,
          //           transaction.transactionTimestamp,
          //         ),
          //       ),
          //     );
          //     endTimestamp =
          //       Math.max(
          //         transaction.transactionTimestamp,
          //         closestTransaction.transactionTimestamp,
          //       ) * 1.00002;
          //   }
          // }
        }

        if (linkedTransactions.length === 2) {
          /**
           * @TODO
           * if buying, start at the start of the green higherT wave
           * end right after the transactions / or right up until the next transaction?
           */
          /**
           * Get transaction timestamp
           * get the candle from data
           * search back for the start of the wave
           * now we have the startTimestamp
           *
           * 1.
           * Now search for the transaction index
           * take the timestamp of the next transaction
           *
           * 2.
           * Or just take an extra 2 candles afterwards
           */
          /** Show start of wave until next transaction or *now* */
          const startOfWaveTimestamp =
            calculateStartOfWaveTimestamp(transactionTimestamp);

          startTimestamp = startOfWaveTimestamp;
          endTimestamp = getEndTimestamp(eventProps.datum.t, startTimestamp);
        }
      }

      /** Fetch the multiOrderCandles from start to end */
      if (!startTimestamp || !endTimestamp || !bot) {
        setIsLoading?.(false);
        return;
      }

      startTimestamp = Math.floor(
        startTimestamp - (endTimestamp - startTimestamp) * 0.05,
      );
      endTimestamp = Math.ceil(
        endTimestamp + (endTimestamp - startTimestamp) * 0.05,
      );

      const range =
        (eventProps.datum.compactTransactionTimestamp ?? endTimestamp) +
        ((eventProps.datum.compactTransactionTimestamp ?? endTimestamp) -
          startTimestamp) *
          0.3;

      const { data } = await axios({
        url: ServicePaths.CANDLE_FETCHER.FetchCandles,
        params: {
          baseAsset: bot.baseAsset,
          quoteAsset: bot.quoteAsset,
          timeFrame: getZoomLevelTimeFrame(zoomLevel),
          startTimestamp,
          endTimestamp: zoomLevel === 4 ? range : endTimestamp,
          includeLatestCandle: true,
        },
      });

      setTemporaryCandles(data);
      setZoomLevelDomainX([startTimestamp, endTimestamp]);
      setZoomLevelRange(range);
      setIsLoading?.(false);
    },
    [
      bot,
      calculateStartOfWaveTimestamp,
      findMultiOrderCandleStartTimestamp,
      getZoomLevelTimeFrame,
      setIsLoading,
      setTemporaryCandles,
      setZoomLevelDomainX,
      setZoomLevelRange,
      temporaryCandles,
      transactions,
      transactionsByTimestamp,
      transactionsSetsLength,
      zoomLevel,
    ],
  );

  return (
    <g>
      {/* {!!data.length && transactionsDataPoints && (
        <VictoryScatter
          {...props}
          data={transactionsDataPoints}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: colors.white,
              strokeWidth: 2,
            },
          }}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: handleZoomIn,
              },
            },
          ]}
          dataComponent={
            <Point
              className={
                !temporaryCandles
                  ? 'single-profit-calcs-chart-scatter-dot'
                  : undefined
              }
            />
          }
          size={5}
        />
      )} */}

      {!!data.length && transactionsDataSets && (
        <VictoryScatter
          {...props}
          data={transactionsDataSets}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: ({ datum }) => datum.strokeColor,
              strokeWidth: ({ datum }) => datum.size / 3,
            },
          }}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: handleTransactionPress,
              },
            },
          ]}
          dataComponent={
            <Point
              className={
                !temporaryCandles
                  ? 'single-profit-calcs-chart-scatter-dot'
                  : undefined
              }
            />
          }
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea
              cornerRadius={3}
              orientation={'left'}
              centerOffset={{ x: -10, y: -40 }}
              pointerOrientation="right"
              pointerWidth={20}
              flyoutStyle={{
                stroke: (d) => d.datum.color,
                strokeWidth: 1.5,
                fill: 'rgba(255, 255, 255, 0.8)',
              }}
              flyoutPadding={{ left: 10, right: 20 }}
              labelComponent={
                <VictoryLabel
                  textAnchor="start"
                  dx={-55}
                  style={[
                    {
                      fill: 'rgb(0, 0, 0)',
                      fontFamily: 'monospace',
                      fontSize: 10,
                      fontWeight: 800,
                    },
                  ]}
                />
              }
            />
          }
          size={(transaction) => transaction.size}
        />
      )}
    </g>
  );
};

export default React.memo(TransactionDotsChart);
